import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ContactForm = ({ className = "" }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    inquiryType: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const inquiryTypes = [
    { value: 'partnership', label: 'Partnership Opportunities' },
    { value: 'media', label: 'Media Requests' },
    { value: 'tourism', label: 'Tourism Information' },
    { value: 'collaboration', label: 'Project Collaboration' },
    { value: 'general', label: 'General Information' },
    { value: 'technical', label: 'Technical Support' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.subject?.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData?.inquiryType) {
      newErrors.inquiryType = 'Please select an inquiry type';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData?.message?.trim()?.length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        subject: '',
        inquiryType: '',
        message: ''
      });
    }, 3000);
  };

  if (isSubmitted) {
    return (
      <div className={`bg-card rounded-lg shadow-card p-8 text-center ${className}`}>
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} className="text-success" />
        </div>
        <h3 className="font-heading font-semibold text-xl text-foreground mb-2">
          Message Sent Successfully!
        </h3>
        <p className="text-muted-foreground mb-4">
          Thank you for your inquiry. We'll get back to you within 24-48 hours.
        </p>
        <div className="text-sm text-muted-foreground">
          Redirecting to form in a few seconds...
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-lg shadow-card p-6 lg:p-8 ${className}`}>
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-xl text-foreground mb-2">
          Send Us a Message
        </h2>
        <p className="text-muted-foreground">
          Have questions about the Creative Twinning project? We'd love to hear from you.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Full Name"
            type="text"
            placeholder="Enter your full name"
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="your.email@example.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />
        </div>

        <Input
          label="Subject"
          type="text"
          placeholder="Brief subject of your inquiry"
          value={formData?.subject}
          onChange={(e) => handleInputChange('subject', e?.target?.value)}
          error={errors?.subject}
          required
        />

        <Select
          label="Inquiry Type"
          placeholder="Select the type of your inquiry"
          options={inquiryTypes}
          value={formData?.inquiryType}
          onChange={(value) => handleInputChange('inquiryType', value)}
          error={errors?.inquiryType}
          required
        />

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            Message <span className="text-destructive">*</span>
          </label>
          <textarea
            placeholder="Please provide details about your inquiry..."
            value={formData?.message}
            onChange={(e) => handleInputChange('message', e?.target?.value)}
            rows={6}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
              errors?.message 
                ? 'border-destructive focus:ring-destructive' :'border-border'
            }`}
          />
          {errors?.message && (
            <p className="text-sm text-destructive">{errors?.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Minimum 10 characters required
          </p>
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
          fullWidth
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending Message...' : 'Send Message'}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;